<script setup>
/** @typedef {import('@/models/Todo').Todo} Todo */
import RemoveButton from './RemoveButton.vue';

// 親から渡されたデータ
/** @type {{ todos: Todo[] }} */
const props = defineProps({
  todos: Array,
  isLoading: Boolean,
  processingId: Number,
});

//removeイベントを親に通知
const emit = defineEmits(['remove', 'update', 'completed']);

// 子から受け取ったclickを受け取り
// todoを親に渡す
function remove(todo) {
  // emitの引数で親に値を渡す
  emit('remove', todo);
}

// 完了のチェックボックスが切り替わった時
async function toggleCompleted(todo, event) {
  const checked = event.target.checked;
  emit('completed', todo, checked);
  return;
}

// 編集モードに切り替え
function editTodo(todo) {
  todo.originalTodo = todo.todo; // 元の値を保持
  todo.isEditing = true;
}

// 編集キャンセル
function cancelEdit(todo) {
  todo.todo = todo.originalTodo;
  todo.isEditing = false;
}

// 編集確定
async function updateTodoHandler(todo) {
  todo.isEditing = false;
  emit('update', todo);
}
</script>

<template>
  <div v-if="isLoading" class="space-y-3">
    <div
      v-for="i in 3"
      :key="i"
      class="flex items-center p-4 bg-gray-50 border border-gray-100 rounded-xl animate-pulse"
    >
      <div class="h-5 w-5 bg-gray-200 rounded-md mr-4"></div>
      <div class="flex-1 space-y-2">
        <div class="h-4 bg-gray-200 rounded w-3/4"></div>
      </div>
    </div>
  </div>
  <TransitionGroup
    tag="ul"
    name="todo-list"
    class="border-4 border-emerald-500 rounded-sm p-4 mt-10 relative min-h-12"
    enter-active-class="transition duration-400 ease-out"
    enter-from-class="transform -translate-x-20 "
    leave-active-class="transition duration-300 ease-in"
    leave-to-class="transform translate-x-30"
  >
    <li
      v-for="todo in props.todos"
      :key="todo.todoId"
      class="p-2 odd:bg-gray-100 hover:bg-gray-200"
      :class="{ 'opacity-50': processingId == todo.todoId }"
    >
      <div class="grid grid-cols-12 my-2 items-center">
        <!-- 編集モード -->
        <template v-if="todo.isEditing">
          <input
            class="col-span-9 self-start"
            v-model="todo.todo"
            @keyup.enter="updateTodoHandler(todo)"
          />
          <button
            class="col-span-1 cursor-pointer rounded border-2 px-1 mx-auto bg-emerald-600 border-emerald-600 text-white font-bold hover:bg-emerald-700 focus:outline-offset-2 focus:outline-4 focus:outline-emerald-400 active:bg-emerald-800"
            @click="updateTodoHandler(todo)"
          >
            保存
          </button>
          <button
            class="col-span-2 cursor-pointer rounded border-2 px-1 mx-auto bg-red-600 border-red-600 text-white font-bold hover:bg-red-700 focus:outline-offset-2 focus:outline-4 focus:outline-red-400 active:bg-red-800"
            @click="cancelEdit(todo)"
          >
            キャンセル
          </button>
        </template>

        <!-- 通常表示モード -->
        <template v-else>
          <!-- 完了チェックボックス -->
          <label class="col-span-1 flex justify-center cursor-pointer">
            <input
              type="checkbox"
              class="peer hidden"
              @change="toggleCompleted(todo, $event)"
              :checked="!!todo.completedAt"
            />
            <div
              class="h-8 w-8 rounded border-2 border-emerald-300 bg-white peer-checked:bg-emerald-500 peer-checked:border-emerald-500 flex items-center justify-center duration-200 ease"
            >
              <svg
                class="w-6 h-6 text-white"
                viewBox="0 0 26 26"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </label>

          <!-- todo本文 -->
          <span
            @click="editTodo(todo)"
            class="col-span-10 self-start"
            :class="{ 'line-through text-gray-400': todo.completedAt }"
            >{{ todo.todo }}</span
          >

          <!-- 削除ボタン -->
          <RemoveButton label="-" @click="remove(todo)" :disabled="processingId" />
        </template>
      </div>
    </li>
    <!-- </ul> -->
  </TransitionGroup>
</template>
<style scoped>
/* 2. 移動アニメーション（これがないと下が詰める時に白が見える） */
.todo-list-move {
  transition: transform 0.6s cubic-bezier(0.55, 0, 0.1, 1);
}

/* 3. 【重要】消える瞬間の設定 */
.todo-list-leave-active {
  /* 浮かせた瞬間に「消える場所」を他の要素が即座に埋められるようにする */
  position: absolute !important;
  width: calc(100% - 4rem);
  z-index: 10;

  /* 消える瞬間に背景が透明にならないよう、現在の背景色を維持 */
  transition: all 0.4s ease-in;
}

/* 4. スケルトンとリストの切り替わりを滑らかにする（おまけ） */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}
</style>
